/**
 * Checkbox — accessible checkbox with optional label rendered as a child.
 *
 * Renders the native input visually-hidden + a custom box, but clicking the
 * label still toggles via the native <label htmlFor> association.
 *
 * Usage:
 *   <Checkbox checked={x} onChange={e => setX(e.target.checked)}>
 *     I agree to the terms
 *   </Checkbox>
 *
 *   <Checkbox checked={x} onChange={...} aria-label="Select all" />
 */

import { forwardRef, useId } from 'react'
import { HiCheck } from 'react-icons/hi2'

const Checkbox = forwardRef(function Checkbox(
  { id, checked, disabled, invalid, className = '', children, ...rest },
  ref
) {
  const autoId = useId()
  const inputId = id || autoId
  return (
    <label
      htmlFor={inputId}
      className={`inline-flex items-start gap-2 cursor-pointer select-none ${disabled ? 'opacity-60 cursor-not-allowed' : ''} ${className}`}
    >
      <span className="relative inline-flex items-center justify-center mt-0.5">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          className="peer absolute opacity-0 w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
          {...rest}
        />
        <span
          className={`
            w-4 h-4 rounded border transition-colors
            ${checked ? 'bg-brand border-brand' : 'bg-surface border-border-strong'}
            ${invalid ? 'border-danger' : ''}
            peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-canvas
          `}
        >
          {checked && <HiCheck className="w-3.5 h-3.5 text-white -mt-0.5 -ms-0.5" aria-hidden="true" />}
        </span>
      </span>
      {children && (
        <span className="text-sm text-ink-secondary leading-tight">{children}</span>
      )}
    </label>
  )
})

export default Checkbox
